import React from 'react';
import { InfoTooltip } from '../common/InfoTooltip';
import type { Analysis } from '../../types/analysis';

interface ClarityAnalysisProps {
  analyses: Analysis[];
}

export const ClarityAnalysis: React.FC<ClarityAnalysisProps> = ({ analyses }) => {
  // Filter analyses that have clarity_analysis
  const analysesWithClarity = analyses.filter(
    (analysis: any) => analysis.clarity_analysis?.calculated_clarity_score !== undefined
  );

  if (analysesWithClarity.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Clarity Analysis</h3>
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No clarity data available yet.</p>
          <p className="text-xs mt-2">Clarity analysis will appear here once available.</p>
        </div>
      </div>
    );
  }

  // Calculate clarity statistics
  const clarityScores = analysesWithClarity.map(
    (analysis: any) => analysis.clarity_analysis.calculated_clarity_score
  );
  const minScore = Math.min(...clarityScores);
  const maxScore = Math.max(...clarityScores);
  const avgScore = clarityScores.reduce((sum, score) => sum + score, 0) / clarityScores.length;

  // Aggregate checklist items
  const checklistCounts: Record<string, { pass: number; warn: number; fail: number }> = {};

  analysesWithClarity.forEach((analysis: any) => {
    const checklist = analysis.clarity_analysis?.clarity_checklist || [];
    checklist.forEach((item: any) => {
      if (!checklistCounts[item.check_name]) {
        checklistCounts[item.check_name] = { pass: 0, warn: 0, fail: 0 };
      }
      const status = item.status.toLowerCase();
      if (status === 'pass') checklistCounts[item.check_name].pass++;
      else if (status === 'warn') checklistCounts[item.check_name].warn++;
      else if (status === 'fail') checklistCounts[item.check_name].fail++;
    });
  });

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pass':
        return 'bg-green-100 text-green-800';
      case 'warn':
        return 'bg-yellow-100 text-yellow-800';
      case 'fail':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Clarity Analysis</h3>
        <InfoTooltip
          title="Clarity Analysis"
          content={[
            "Clarity scores measure how clear and understandable your communications are (out of 10).",
            "Higher scores indicate more concrete language, better structure, and simpler expressions.",
            "The checklist tracks common clarity issues across all your communications."
          ]}
          position="left"
        />
      </div>

      <div className="space-y-6">
        {/* Overall Clarity Score */}
        <div className="relative">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-gray-900">Overall Clarity</h4>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  {avgScore.toFixed(1)}/10
                </span>
              </div>
              <p className="text-sm text-gray-500">Average across all communications</p>
            </div>
          </div>

          {/* Score range visualization */}
          <div className="relative h-2 bg-gray-200 rounded-full overflow-visible">
            <div
              className="absolute h-1 bg-blue-500 top-0.5 rounded-full"
              style={{
                left: `${(minScore / 10) * 100}%`,
                width: `${((maxScore - minScore) / 10) * 100}%`,
              }}
            />
            <div
              className="absolute w-3 h-3 bg-blue-700 rounded-full border-2 border-white shadow-sm"
              style={{
                left: `${(avgScore / 10) * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </div>

          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>Min: {minScore.toFixed(1)}</span>
            <span>Avg: {avgScore.toFixed(1)}</span>
            <span>Max: {maxScore.toFixed(1)}</span>
          </div>
        </div>

        {/* Clarity Checklist */}
        {Object.entries(checklistCounts).map(([checkName, counts]) => {
          const total = counts.pass + counts.warn + counts.fail;
          return (
            <div key={checkName} className="relative">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{checkName}</h4>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {counts.pass > 0 && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor('pass')}`}>
                    Pass: {counts.pass}
                  </span>
                )}
                {counts.warn > 0 && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor('warn')}`}>
                    Warn: {counts.warn}
                  </span>
                )}
                {counts.fail > 0 && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor('fail')}`}>
                    Fail: {counts.fail}
                  </span>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  Total: {total}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
